import { defineBlock } from "eddev/blocks"
import { Col, Container, Grid } from "@components/layout/Grid"
import { NRMAC, RACVC, RACQC, RAAC, RACC, RACTC, AANTC, Logo } from "@components/atoms/Logo"
import { cn } from "@utils/tw"
import { useClubs } from "@hooks/queries"
import { Heading } from "@components/atoms/Heading"
import { Typography } from "@components/atoms/Typography"
import { Button } from "@components/atoms/Button"
import { useBreakpointIsFrom, useIsMobile } from "@hooks/useViewportDerived"
import { ClubFragment } from "../../types.graphql"
import { ComponentPropsWithoutRef, forwardRef, Fragment, PropsWithChildren } from "react"
import { Link, useHydrating } from "eddev/routing"

export const meta: BlockMeta = {
  title: "Club list",
  category: "common",
  icon: "editor-ul",
  keywords: ["clubs"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

const ClubLogoLink = (props: PropsWithChildren & ClubFragment) => {
  if (!props.pagereferenced?.link) return <>{props.children}</>
  return <Link href={`${props.pagereferenced?.link.replace(/\/$/, "")}#${props.name}`}>{props.children}</Link>
}

export default defineBlock("general/club-list", (props) => {
  const clubs = useClubs(undefined)
  // const isLoading = clubs.loading()
  const isMobile = useIsMobile()
  const hydrating = useHydrating()

  const getLogo = (name: string) => {
    switch (name) {
      case "NRMA":
        return <NRMAC />

      case "RACV":
        return <RACVC />

      case "RACQ":
        return <RACQC />

      case "RAA":
        return <RAAC />

      case "RAC":
        return <RACC />

      case "RACT":
        return <RACTC />

      case "AANT":
        return <AANTC />

      default:
        return <Logo />
    }
  }

  const isLaptop = useBreakpointIsFrom("laptop")
  let nrma = false
  return (
    <ClubListContainer>
      <Grid>
        {props.onlyLogos ? (
          <>
            {isLaptop && <Col initial={1} />}
            <Col initial={12} laptop={10}>
              <LogoContainer>
                {!hydrating &&
                  clubs.data?.siteSettings?.themeSiteSettings?.clubs?.map((club, i) => {
                    if (nrma && club?.name === "NRMA") return <Fragment key={i} />
                    if (club?.name === "NRMA") nrma = true
                    return (
                      <ClubLogo key={i}>
                        <ClubLogoLink {...club!}>{getLogo(club!.name!)}</ClubLogoLink>
                      </ClubLogo>
                    )
                  })}
              </LogoContainer>
            </Col>
          </>
        ) : (
          <>
            {!isMobile && <Col initial={12} tablet={1} laptop={2} />}
            <ClubCol initial={12} tablet={10} laptop={8}>
              {!hydrating &&
                clubs.data?.siteSettings?.themeSiteSettings?.clubs?.map((club, i) => (
                  <ClubCard key={i} id={club?.name!}>
                    <Left>
                      <Content>
                        {isMobile && <MobileLogo>{getLogo(club!.name!)}</MobileLogo>}

                        <InnerContent>
                          <Heading as="h3">{club?.name}</Heading>
                          {club?.location && (
                            <Typography
                              as="span"
                              variant="caps"
                              dangerouslySetInnerHTML={{
                                __html: isMobile ? club.location.replace(" / ", "<br />") : club.location,
                              }}
                            />
                          )}
                        </InnerContent>
                      </Content>
                      <Contacts>
                        {club?.website && (
                          <Button
                            color="yellow"
                            size="medium"
                            href={club.website}
                            target="_blank"
                            icon="ARROW_UP_RIGHT"
                            iconStyle="left"
                          >
                            {club.name}
                          </Button>
                        )}
                        {club?.phone && (
                          <Button
                            color={isMobile ? "white" : "grey"}
                            size="medium"
                            href={`tel:${club.phone.replaceAll(" ", "")}`}
                            icon="PHONE"
                            iconStyle="left"
                          >
                            {club.phone}
                          </Button>
                        )}
                        {club?.idpStoreFinderLink && (
                          <Button
                            color={isMobile ? "white" : "grey"}
                            size="medium"
                            href={club.idpStoreFinderLink}
                            target="_blank"
                            icon="MOTORING_ALT"
                            iconStyle="left"
                          >
                            Locate branch
                          </Button>
                        )}
                      </Contacts>
                    </Left>
                    {!isMobile && <Right>{getLogo(club!.name!)}</Right>}
                  </ClubCard>
                ))}
            </ClubCol>
          </>
        )}
      </Grid>
    </ClubListContainer>
  )
})

const ClubListContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => <Container ref={ref} className={cn("my-4", className)} {...props} />,
)
ClubListContainer.displayName = "ClubListContainer"

const LogoContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex gap-5 p-5 flex-wrap bg-light-grey rounded-md justify-center", className)}
    {...props}
  />
))
LogoContainer.displayName = "LogoContainer"

const ClubLogo = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-white rounded-[8px] p-3 flex items-center justify-center w-[calc((100%-var(--spacing-5))*(1/2))]",
      "[&_svg]:w-full [&_svg]:h-auto",
      "sm:w-[calc((100%-(var(--spacing-5)*2))*(1/3))]",
      "md:w-[calc((100%-(var(--spacing-5)*6))*(1/7))]",
      className,
    )}
    {...props}
  />
))
ClubLogo.displayName = "ClubLogo"

const ClubCol = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Col>>(({ className, ...props }, ref) => (
  <Col ref={ref} className={cn("flex flex-col", className)} {...props} />
))
ClubCol.displayName = "ClubCol"

const ClubCard = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex gap-8 my-4 py-5 px-6 justify-between bg-light-grey rounded-md", className)}
    {...props}
  />
))
ClubCard.displayName = "ClubCard"

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col justify-between gap-8 grow", className)} {...props} />
))
Left.displayName = "Left"

const Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-row gap-2 md:flex-col", className)} {...props} />
))
Content.displayName = "Content"

const MobileLogo = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-white rounded-[6px] overflow-hidden min-w-[56px] w-[56px] h-[56px] mr-4",
      "[&_svg]:w-[56px] [&_svg]:h-[56px]",
      className,
    )}
    {...props}
  />
))
MobileLogo.displayName = "MobileLogo"

const InnerContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1 justify-center", className)} {...props} />
))
InnerContent.displayName = "InnerContent"

const Contacts = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex gap-2 flex-col w-full",
      "[&_a.button_span]:order-1 [&_a.button_span]:grow [&_a.button_span]:text-left [&_a.button_svg]:order-2",
      "md:flex-row",
      "md:[&_a.button_span]:order-2 md:[&_a.button_span]:grow-0 md:[&_a.button_span]:text-center md:[&_a.button_svg]:order-1",
      className,
    )}
    {...props}
  />
))
Contacts.displayName = "Contacts"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-white rounded-[6px] overflow-hidden min-w-[120px] p-4 [&_svg]:w-[120px] [&_svg]:h-[120px]",
      className,
    )}
    {...props}
  />
))
Right.displayName = "Right"
